import { ReactNode, useMemo, useState } from 'react';
import styles from '../TeamAccordionItem.module.scss';
import { TeamAccordionItemAction, TeamAccordionItemActionsProps } from '../TeamAccordionItem.types';
import { useTeamMembersContext } from '../../../../../lib/context/TeamMembersContext';
import { useSnackbarContext } from '../../../../../lib/context/SnackbarContext';
import { NotificationType } from '../../Notification/Notification';
import Button from '../../../../common/Button/Button';
import SplitButton from '../../../../common/SplitButton/SplitButton';
import TeamAccordionItemModal from '../TeamAccordionItemModal';
import { replacePlaceholder } from '../../../../../lib/utils/placeholders/replacePlaceholder';
import CreateSubTeamAndRenameDialog from './CreateSubTeamAndRenameDialog';
import DeleteTeamDialog from './DeleteTeamDialog';
import getTeamProperties from '../../../../../lib/utils/getTeamProperties';
import useFeature from '../../../../../lib/hooks/useFeature';
import { FeatureToggleCodename } from '../../../../../lib/types/enums';

const TeamAccordionItemActions = ({ teamMeta, onUserModification }: TeamAccordionItemActionsProps) => {
  const [dialogOpened, setDialogOpened] = useState<TeamAccordionItemAction | null>(null);
  const {
    dialogTitle,
    buttonLabel,
    noItemsSelectedMessage,
    successMessage,
    createSubteamButtonLabel,
    deleteTeamButtonLabel,
    renameTeamButtonLabel,
  } = useTeamMembersContext();
  const { triggerSnackbar } = useSnackbarContext();
  const subteamManagementEnabled = useFeature(FeatureToggleCodename.subteamManagement);
  const teamProperties = getTeamProperties(teamMeta);

  const splitButtonOptions = useMemo(
    () => [
      { label: buttonLabel, value: TeamAccordionItemAction.ADD_MEMBER },
      { label: createSubteamButtonLabel, value: TeamAccordionItemAction.CREATE_SUBTEAM },
      ...(teamProperties?.editable
        ? [
            { label: deleteTeamButtonLabel, value: TeamAccordionItemAction.DELETE_TEAM },
            { label: renameTeamButtonLabel, value: TeamAccordionItemAction.RENAME_TEAM },
          ]
        : []),
    ],
    [buttonLabel, createSubteamButtonLabel, deleteTeamButtonLabel, renameTeamButtonLabel, teamProperties?.editable],
  );

  const handleAddMemberSuccess = (fullSuccess: boolean = true) => {
    onUserModification();

    if (fullSuccess) {
      triggerSnackbar(successMessage, NotificationType.Success);
      setDialogOpened(null);
    }
  };

  const commonConfirmDialogProps = useMemo(
    () => ({
      onClose: () => setDialogOpened(null),
      teamMeta,
    }),
    [teamMeta],
  );

  const dialogsMap: Record<TeamAccordionItemAction, ReactNode> = {
    [TeamAccordionItemAction.ADD_MEMBER]: (
      <TeamAccordionItemModal
        teamId={teamMeta.id}
        onSuccess={handleAddMemberSuccess}
        onCancel={() => setDialogOpened(null)}
        isModalOpened
        dialogTitle={replacePlaceholder(teamMeta, dialogTitle)}
        noItemsSelectedMessage={noItemsSelectedMessage}
      />
    ),
    [TeamAccordionItemAction.CREATE_SUBTEAM]: (
      <CreateSubTeamAndRenameDialog {...commonConfirmDialogProps} type={TeamAccordionItemAction.CREATE_SUBTEAM} />
    ),
    [TeamAccordionItemAction.DELETE_TEAM]: <DeleteTeamDialog {...commonConfirmDialogProps} />,
    [TeamAccordionItemAction.RENAME_TEAM]: (
      <CreateSubTeamAndRenameDialog {...commonConfirmDialogProps} type={TeamAccordionItemAction.RENAME_TEAM} />
    ),
  };

  return (
    <>
      <div className={styles.addMemberContainer}>
        {teamProperties?.createSubteamAllowed && subteamManagementEnabled ? (
          <SplitButton options={splitButtonOptions} onOptionSelect={value => setDialogOpened(value)} />
        ) : (
          <Button
            className={styles.addMemberButton}
            onClick={() => setDialogOpened(TeamAccordionItemAction.ADD_MEMBER)}
          >
            {buttonLabel}
          </Button>
        )}
      </div>
      {!!dialogOpened && dialogsMap[dialogOpened]}
    </>
  );
};
export default TeamAccordionItemActions;
